import {
  Dialog,
  DialogProps,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Button } from "components/button";
import { CheckCircleIcon, InfoIcon, ReportIcon } from "components/icons";
import React from "react";
import { convertStringToSentenceCase } from "utilz/helpers";

interface ModalProps extends DialogProps {
  title?: string;
  /**
   * Skip the automatic sentence-casing of `title`. Use when the title embeds a
   * value whose casing matters (e.g. a ticket id like STBIN07172600002).
   */
  keepOriginalCasing?: boolean;
  tagline?: string | React.ReactNode;
  showIcon?: boolean;
  isPrimaryButtonDisabled?: boolean;
  isSecondaryButtonDisabled?: boolean;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryButtonClick?: () => void;
  onSecondaryButtonClick?: () => void;
  paperStyles?: React.CSSProperties;
  type?: "primary" | "error" | "warning" | "success" | string;
  isLoading?: boolean;
  customIcon?: React.ReactNode;
  hideSecondaryButton?: boolean;
  primaryButtonType?: "primary" | "error";
  footer?: React.ReactNode;
}

export const ConfirmationModal = ({
  title = "Delete Confirmation",
  keepOriginalCasing = false,
  tagline = "Are you sure you want to delete?",
  open,
  showIcon = true,
  paperStyles,
  onClose,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  primaryButtonText = "Delete",
  secondaryButtonText = "Cancel",
  type = "error",
  isLoading = false,
  customIcon,
  hideSecondaryButton,
  primaryButtonType,
  footer,

  ...rest
}: ModalProps) => {
  const theme = useTheme();

  const getTintColor = (type: string) => {
    switch (type) {
      case "error":
        return theme.palette.error.tint;

      case "success":
        return theme.palette.success.tint;

      case "warning":
        return theme.palette.warning.tint;

      case "primary":
        return theme.palette.secondary["purple-tint-3"];
      case "info":
        return theme.palette.info.tint;

      default:
        return theme.palette.success.tint;
    }
  };
  return (
    <Dialog
      open={open}
      // maxWidth={false}
      PaperProps={{
        style: {
          borderRadius: "8px",
          padding: "24px",
          width: "524px",
          ...paperStyles,
        },
      }}
      onClose={onClose}
      sx={{ display: "flex", flexDirection: "column" }}
      {...rest}
    >
      {showIcon && (
        <Stack
          sx={{
            width: "56px",
            height: "56px",
            backgroundColor: getTintColor(type),
            borderRadius: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {customIcon ? customIcon : <CardIcon type={type} />}
        </Stack>
      )}

      <Typography mt={showIcon ? "16px" : "0px"} variant="h5">
        {typeof title === "string" && !keepOriginalCasing
          ? convertStringToSentenceCase(title)
          : title}
      </Typography>
      <Typography
        sx={{ mt: "16px" }}
        variant="body-large"
        color={theme.palette.lightBg.low}
      >
        {tagline}
      </Typography>
      {footer ? (
        footer
      ) : (
        <Stack direction="row" mt="24px" gap="16px" justifyContent="flex-end">
          {!hideSecondaryButton && (
            <Button
              disabled={isLoading}
              variant="secondary"
              onClick={onSecondaryButtonClick}
            >
              {secondaryButtonText}
            </Button>
          )}

          <Button
            loading={isLoading}
            variant="primary"
            onClick={onPrimaryButtonClick}
            sx={{
              ...(primaryButtonType === "error"
                ? {
                    bgcolor: theme.palette.error.main,
                    "&:hover": { backgroundColor: "#A9050E" },
                  }
                : {}),
            }}
          >
            {primaryButtonText}
          </Button>
        </Stack>
      )}
    </Dialog>
  );
};

const CardIcon = ({ type }: { type: "success" | "error" | string }) => {
  const theme = useTheme();
  switch (type) {
    case "success":
      return <CheckCircleIcon htmlColor={theme.palette.success.main} />;

    case "error":
      return (
        <Stack
          sx={{
            borderRadius: "100%",
            width: "24px",
            height: "24px",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: theme.palette.error.main,
          }}
        >
          <ReportIcon
            fontSize="small"
            sx={{ width: "18px", height: "18px" }}
            htmlColor={"#fff"}
          />
        </Stack>
      );

    case "warning":
      return <ReportIcon htmlColor={theme.palette.warning.main} />;

    case "info":
      return <InfoIcon htmlColor={theme.palette.info.main} />;

    default:
      return <CheckCircleIcon htmlColor={theme.palette.success.main} />;
  }
};
